// Everything an app-contributed button needs that is not its appearance.
//
// The product draws the control, so nothing here loads a document and
// `SlotContainer` is not involved. A `workflow` button runs one of the app's
// workflows and a `call` button one of its worker.js handlers: both POST
// cross-origin to the apps host on the cookie `useAppsSession` mints. A
// `navigate` button makes no request at all, so it needs no session and renders
// as soon as assignments load. A surface rendering these decides
// only how they look — `AppButtonMenu` is the standard answer, and a page that
// wants one named app's button and no menu calls this directly.

import { t } from '@lingui/core/macro';
import {
    type ButtonWidget,
    callUrl,
    resolveLocalizedName,
    type SlotAssignmentView,
    type SlotId,
    WORKFLOW_ID_PLACEHOLDER,
} from '@luminovo/apps-types';
import { createSnackbar } from '@luminovo/design-system';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSlotAnalytics, widgetAttribution } from '../analytics/slotAnalytics';
import { useEffectHandlers } from '../effects/useEffectHandlers';
import { useSlotErrorReporting } from '../errors/slotErrorReporting';
import { reportWidgetSignal } from '../observability/reportWidgetSignal';
import { fillNavigateTarget } from '../routing/fillNavigateTarget';
import { useAppsSession } from './useAppsSession';
import { useSlotAssignments } from './useSlotAssignments';

type ButtonSlotAssignment = Omit<SlotAssignmentView, 'widget'> & { widget: ButtonWidget };

/** One app button this slot offers, ready to render however the surface likes. */
export interface SlotButton {
    /** Stable across renders; unique within the slot. */
    key: string;
    appId: string;
    appName: string;
    widgetId: string;
    /** The app-authored label, resolved for the locale. */
    name: string;
    isRunning: boolean;
    run: () => void;
}

// Withheld rather than left to fail on click.
function missingRequiredParams(widget: ButtonWidget, params: Readonly<Record<string, unknown>>): string[] {
    return (widget.paramsSchema.required ?? []).filter((name) => params[name] === undefined);
}

// Mirrors the closed outcome set the widget SDK keys on the same statuses, so one
// failure does not read two ways depending on which surface ran the workflow.
function failureMessage(status: number): string {
    if (status === 404) return t`This app no longer offers that action.`;
    if (status === 429) return t`Too many requests. Try again in a moment.`;
    if (status === 400) return t`The app rejected this request.`;
    return t`The action could not be completed.`;
}

const assignmentKey = (assignment: ButtonSlotAssignment): string => `${assignment.app.id}:${assignment.widget.id}`;

// A button that passed every check, with what its click does already resolved.
// `workflow` and `call` both POST to `runUrl`; `REQUESTS` below holds how they
// differ.
type UsableButton = { key: string; name: string; assignment: ButtonSlotAssignment } & (
    | { type: 'workflow' | 'call'; runUrl: string }
    | { type: 'navigate'; path: string }
);
type RequestButton = UsableButton & { type: 'workflow' | 'call' };

// Buttons already offered this page session, so `apps/button_shown` counts reach
// rather than re-renders. Module-level for the reason `useSlotAssignments` keeps
// its own set there: a component remounts on every navigation.
const shownButtons = new Set<string>();

/** Test seam — module-level state would otherwise leak between cases. */
export function resetShownButtonsForTest(): void {
    shownButtons.clear();
}

// One retry is the whole budget: the cookie either re-mints and the run goes
// through, or something is wrong that trying again cannot fix.
const MAX_SESSION_RECOVERIES = 1;

// How a workflow or call button talks to its route: everything that differs
// between the two, side by side, so `run` reads the same for both.
interface RequestKind {
    /** What the route takes as its body, given the slot params. */
    body: (params: Readonly<Record<string, unknown>>) => unknown;
    headers: Readonly<Record<string, string>>;
    /** The object in a successful answer that the effects sit on. */
    effectsOf: (answer: unknown) => unknown;
    /** An answer that is not JSON is the app's fault, rather than a run with nothing to do. */
    answerIsTheApps: boolean;
    /**
     * Whether a failed answer is ours to answer for, so Luminovo hears about it.
     * Everything else — a workflow the app removed, a tenant over its budget, input
     * the app refused — is the author's or the tenant's, and reporting it would fill
     * our Sentry with other people's bugs.
     */
    isPlatformFault: (status: number, answer: unknown) => boolean;
}

// The error codes the call route and the dispatcher answer for themselves, by
// status. Anything else on a failed call is the handler's own status and body,
// relayed: a handler that throws comes back as a 500, and one proxying a backend
// may answer 502 or 503 on its own. Two cases still read as ours, since the
// route answers them with the code it uses for a broken dispatch: a handler's
// 401 (the route turns it into a 502, so the browser does not re-mint its
// session) and a handler's own 502 with a body that is not JSON.
const PLATFORM_CALL_ERRORS: Readonly<Partial<Record<number, readonly string[]>>> = {
    502: ['dispatch_failed', 'dispatch_misconfigured'],
    503: ['apps_runner_unavailable'],
};

const asObject = (value: unknown): Record<string, unknown> | undefined =>
    typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : undefined;

const REQUESTS: Readonly<Record<RequestButton['type'], RequestKind>> = {
    // The run route takes `{ input }` and answers the executor's envelope, which
    // keeps what the workflow returned under `result`. The envelope is the
    // platform's, so every 5xx is too.
    workflow: {
        body: (params) => ({ input: params }),
        headers: {},
        effectsOf: (answer) => asObject(answer)?.result,
        answerIsTheApps: false,
        isPlatformFault: (status) => status >= 500,
    },
    // The call route hands its body to the handler as `input`, so `{ input }`
    // would arrive nested, and answers the handler's own JSON with no envelope.
    call: {
        body: (params) => params,
        // What a button can use. The route does not enforce it: a handler that
        // streams anyway is read to its end and reported as not JSON.
        headers: { accept: 'application/json' },
        effectsOf: (answer) => answer,
        answerIsTheApps: true,
        isPlatformFault: (status, answer) => {
            const code = asObject(answer)?.error;
            return typeof code === 'string' && (PLATFORM_CALL_ERRORS[status] ?? []).includes(code);
        },
    },
};

const NOT_JSON = Symbol('not JSON');

/**
 * The app buttons a slot offers, in a stable order, each able to run itself.
 *
 * A workflow or call button is left out until the apps session is ready, so a
 * surface never shows a control that could only 401. A navigate button needs no
 * session.
 */
export function useSlotButtons(
    slotId: SlotId,
    params: Readonly<Record<string, unknown>>,
    locale: string,
): readonly SlotButton[] {
    const { data } = useSlotAssignments(slotId);
    // Where a navigate button goes. Its destination is one of the app's own
    // full-page widgets, which live in `slots.full-page`, not in this slot. That
    // slot is already fetched on every page (the command palette, the app
    // layout), so this shares the cached response rather than adding a request.
    // It is also the served set: a widget the platform does not render is
    // absent here, so a button to it is withheld instead of linking to a 404.
    const { data: fullPage } = useSlotAssignments('slots.full-page');
    const reportError = useSlotErrorReporting();
    const track = useSlotAnalytics();
    const { enqueueSnackbar } = useSnackbar();
    const [running, setRunning] = useState<string | undefined>(undefined);
    const reportedRef = useRef(new Set<string>());
    const recoveriesRef = useRef(0);

    const { usable, misconfigured } = useMemo(() => {
        const collator = new Intl.Collator(locale);
        const found: UsableButton[] = [];
        const skipped: Array<{ key: string; reason: string }> = [];

        for (const assignment of data?.assignments ?? []) {
            if (assignment.widget.kind !== 'button') continue;
            const button = assignment as ButtonSlotAssignment;
            const key = assignmentKey(button);
            const missing = missingRequiredParams(button.widget, params);
            if (missing.length > 0) {
                skipped.push({ key, reason: `needs params ${slotId} does not supply: ${missing.join(', ')}` });
                continue;
            }
            const name = resolveLocalizedName(button.widget.name, locale);
            const { action } = button.widget;
            if (action.type === 'navigate') {
                // Not known yet, so neither offered nor reported.
                if (fullPage === undefined) continue;
                const destination = fullPage.assignments.find(
                    (candidate) =>
                        candidate.app.id === button.app.id &&
                        candidate.widget.kind === 'full-page' &&
                        candidate.widget.id === action.to.widget,
                );
                if (destination === undefined || destination.widget.kind !== 'full-page') {
                    skipped.push({
                        key,
                        reason: `navigates to full-page widget "${action.to.widget}", which is not served here`,
                    });
                    continue;
                }
                const route = destination.widget.route;
                const target = fillNavigateTarget(route, action, params);
                if ('missing' in target) {
                    skipped.push({ key, reason: `has no value for ${target.missing.join(', ')} to navigate with` });
                    continue;
                }
                found.push({ type: 'navigate', key, assignment: button, name, path: target.path });
                continue;
            }
            if (action.type === 'call') {
                // The handler runs on the same apps host every other URL here is
                // pinned to; without one there is nowhere to call.
                const appsOrigin = data?.appsOrigin;
                if (appsOrigin === undefined) {
                    skipped.push({ key, reason: 'has no apps host to call its handler on' });
                    continue;
                }
                found.push({
                    type: 'call',
                    key,
                    assignment: button,
                    name,
                    runUrl: callUrl(appsOrigin, button.app.id, action.handler),
                });
                continue;
            }
            const template = button.workflow_run_url_template;
            if (template === undefined) {
                skipped.push({ key, reason: 'has no workflow run URL to POST to' });
                continue;
            }
            found.push({
                type: 'workflow',
                key,
                assignment: button,
                name,
                runUrl: template.replace(WORKFLOW_ID_PLACEHOLDER, encodeURIComponent(action.workflowId)),
            });
        }

        // The assignments API promises no ordering, so the host imposes one.
        found.sort((a, b) => collator.compare(a.name, b.name) || a.key.localeCompare(b.key));
        return { usable: found, misconfigured: skipped };
    }, [data, fullPage, locale, params, slotId]);

    const appsOrigin = data?.appsOrigin;
    useEffect(() => {
        if (appsOrigin === undefined) return;
        for (const { key, assignment } of usable) {
            const shownKey = `${slotId}:${key}`;
            if (shownButtons.has(shownKey)) continue;
            shownButtons.add(shownKey);
            track('apps/button_shown', widgetAttribution(assignment, slotId));
        }
    }, [appsOrigin, usable, slotId, track]);

    useEffect(() => {
        for (const { key, reason } of misconfigured) {
            if (reportedRef.current.has(key)) continue;
            reportedRef.current.add(key);
            reportError(new Error(`Widget ${key} ${reason}`), { level: 'warning', tags: { slotId } });
        }
    }, [misconfigured, reportError, slotId]);

    // Only a workflow or call button makes a request, and so needs the apps
    // cookie. A slot of navigate buttons never asks for one.
    const needsSession = usable.some((button) => button.type !== 'navigate');
    const appsSession = useAppsSession(needsSession ? appsOrigin : undefined);
    const sessionReady = appsSession.data?.status === 'ready';
    const effects = useEffectHandlers();

    const run = useCallback(
        async (button: RequestButton): Promise<void> => {
            const { assignment, name, runUrl, key } = button;
            const { app, widget } = assignment;
            const kind = REQUESTS[button.type];
            const attribution = widgetAttribution(assignment, slotId);
            const signalContext = { appId: app.id, widgetId: widget.id, slotId, appsOrigin: appsOrigin ?? '' };
            const fail = (status: number): void => {
                enqueueSnackbar(
                    ...createSnackbar({
                        title: app.name,
                        message: failureMessage(status),
                        overrides: { notistackSnackbarOptions: { variant: 'error' } },
                    }),
                );
            };
            setRunning(key);
            track('apps/button_clicked', attribution);
            try {
                const post = (): Promise<Response> =>
                    fetch(runUrl, {
                        method: 'POST',
                        // The apps-host cookie IS the credential, and this request is
                        // cross-origin to it, so it has to be asked for explicitly.
                        credentials: 'include',
                        headers: { 'content-type': 'application/json', 'x-lumi-app': app.id, ...kind.headers },
                        body: JSON.stringify(kind.body(params)),
                    });

                let response = await post();
                // The cookie outlives neither the page nor the session store, so a table
                // left open long enough answers 401 on every click until something
                // re-mints it. The frame path recovers the same way.
                if (response.status === 401 && recoveriesRef.current < MAX_SESSION_RECOVERIES) {
                    recoveriesRef.current += 1;
                    const minted = await appsSession.refetch();
                    if (minted.data?.status === 'ready') {
                        response = await post();
                    }
                }
                if (response.status !== 401) {
                    recoveriesRef.current = 0;
                } else if (recoveriesRef.current >= MAX_SESSION_RECOVERIES) {
                    reportWidgetSignal('button_session_unrecoverable', signalContext);
                }
                if (!response.ok) {
                    const failure: unknown = await response.json().catch(() => undefined);
                    track('apps/button_completed', { ...attribution, outcome: 'failed', status: response.status });
                    if (kind.isPlatformFault(response.status, failure)) {
                        reportWidgetSignal('button_run_unreachable', { ...signalContext, runStatus: response.status });
                    }
                    fail(response.status);
                    return;
                }
                const answer: unknown = await response.json().catch(() => NOT_JSON);
                // Nothing to act on and no way to tell whether it worked, so the user
                // hears that it did not, and the author through the warning.
                if (answer === NOT_JSON && kind.answerIsTheApps) {
                    reportError(new Error(`Widget ${key} answered with something other than JSON`), {
                        level: 'warning',
                        tags: { slotId },
                    });
                    track('apps/button_completed', { ...attribution, outcome: 'failed', status: response.status });
                    fail(response.status);
                    return;
                }
                const { applied, malformed } = effects.applyEffects(kind.effectsOf(answer), { appId: app.id });
                for (const effect of malformed) {
                    reportError(new Error(`Widget ${key} returned an unusable "${effect}" effect`), {
                        level: 'warning',
                        tags: { slotId },
                    });
                }
                track('apps/button_completed', {
                    ...attribution,
                    outcome: applied.includes('navigate')
                        ? 'navigated'
                        : applied.includes('download')
                          ? 'file'
                          : 'done',
                });
                // The effect is the feedback: the user is on a new page or holding a file.
                if (applied.length > 0) return;
                enqueueSnackbar(
                    ...createSnackbar({
                        title: app.name,
                        message: t`${name} finished.`,
                        overrides: { notistackSnackbarOptions: { variant: 'success' } },
                    }),
                );
            } catch (cause) {
                // Never reached the route at all, so there is no status to judge by and
                // it is ours until proven otherwise. The rejection travels with the signal
                // so `beforeSend` can still recognise a click made offline.
                track('apps/button_completed', { ...attribution, outcome: 'failed' });
                reportWidgetSignal('button_run_unreachable', { ...signalContext, cause });
                fail(0);
            } finally {
                setRunning(undefined);
            }
        },
        [appsOrigin, appsSession, effects, enqueueSnackbar, params, reportError, slotId, track],
    );

    const go = useCallback(
        (button: UsableButton & { type: 'navigate' }): void => {
            const attribution = widgetAttribution(button.assignment, slotId);
            track('apps/button_clicked', attribution);
            effects.navigate({ path: button.path }, { appId: button.assignment.app.id });
            track('apps/button_completed', { ...attribution, outcome: 'navigated' });
        },
        [effects, slotId, track],
    );

    return useMemo(
        () =>
            usable
                // A workflow or call button waits for the session, so no click can 401.
                .filter((button) => button.type === 'navigate' || sessionReady)
                .map((button) => ({
                    key: button.key,
                    appId: button.assignment.app.id,
                    appName: button.assignment.app.name,
                    widgetId: button.assignment.widget.id,
                    name: button.name,
                    isRunning: running === button.key,
                    run: () => {
                        if (button.type === 'navigate') go(button);
                        else void run(button);
                    },
                })),
        [go, run, running, sessionReady, usable],
    );
}
